// Hidden /au route — Australia positioning, pre-built per spec §6 ("Hidden-but-routed,
// GeoLens convention; unlinked until the 2027 runway activates").
// NOT referenced from nav or footer. Search engines indexed only after launch.
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Section, Overline, PrimaryCTA, SecondaryCTA, StatLine } from "@/components/Section";
import WaitlistForm from "@/components/WaitlistForm";
import HeroContinuum from "@/components/HeroContinuum";
import ContinuumBand from "@/components/ContinuumBand";
import NoIndex from "@/components/NoIndex";

const AU_PROOF = [
  { k: "Methodology", v: "Versioned & documented" },
  { k: "Data residency", v: "Australian-resident option" },
  { k: "Sign-off", v: "Named professional" },
  { k: "Pricing", v: "Published, AUD-quoted" },
];

const USECASES = [
  {
    t: "LGAs & state planning",
    d: "Housing & community-needs evidence aligned to state-level reporting cycles. Versioned methodology, comparability across council areas.",
  },
  {
    t: "PHNs & community health",
    d: "Service-landscape mapping and indicator panels for Primary Health Networks and community-health commissioning.",
  },
  {
    t: "Foundations & philanthropy",
    d: "The disparity layer — funding flows vs. indicator-level need — adapted to Australian charity (ACNC) registers.",
  },
];

export default function AU() {
  return (
    <>
      <NoIndex />
      {/* Hero — Australia variant */}
      <section data-testid="au-hero" className="relative bg-[#0A1224] text-white overflow-hidden">
        <ContinuumBand className="absolute top-0 left-0 right-0 h-1" testid="au-hero-band" />
        <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-32 md:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <Overline className="text-slate-400 mb-8">
                Continuum · Australia · Early-access programme
              </Overline>
              <h1 className="text-5xl md:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.92]">
                The same evidence base, <br className="hidden md:block" />
                <span className="text-[#FF8A00]">tuned for Australia.</span>
              </h1>
              <p className="text-lg md:text-2xl text-slate-300 mt-10 max-w-3xl leading-relaxed">
                Continuum is rolling out an Australia programme in 2027 — LGAs, PHNs,
                foundations, and state-level planning bodies. Same methodology, same
                versioning discipline, residency in Australian regions.
              </p>
              <div className="flex flex-wrap gap-3 mt-10">
                <PrimaryCTA to="/contact" testid="au-hero-cta">Register Australian interest</PrimaryCTA>
                <SecondaryCTA to="/method" testid="au-hero-method-cta" dark>Read the method</SecondaryCTA>
              </div>
            </div>
            <div className="lg:col-span-4 reveal reveal-d2">
              <div className="bg-white p-8 border-t-2 border-[#FF8A00]">
                <Overline className="mb-3">2027 cohort · Early access</Overline>
                <p className="text-slate-600 text-sm mb-5 leading-relaxed">
                  We're seeding the Australia programme from the waitlist. Register your
                  organisation to receive cohort updates and pricing locks.
                </p>
                <WaitlistForm compact testid="au-waitlist-form" />
              </div>
            </div>
          </div>

          <div className="mt-20 reveal reveal-d3">
            <HeroContinuum />
          </div>
        </div>
      </section>

      {/* Trust strip — AU localisation */}
      <Section className="!py-16" testid="au-trust-strip">
        <div data-testid="au-proof-strip" className="border-y border-slate-200 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {AU_PROOF.map((it) => (
              <div key={it.k} className="border-l-2 border-[#FF8A00] pl-4">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">{it.k}</div>
                <div className="text-[#0A1224] font-medium mt-1">{it.v}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Use cases */}
      <Section testid="au-usecases">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <Overline className="mb-6">Where Continuum fits in AU</Overline>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-tight">
              Three lenses. <span className="text-[#FF8A00]">One evidence base.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed text-lg">
              The Australia rollout starts narrow on purpose. We're prioritising depth in
              three buyer types before broadening — same discipline we applied in Canada.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-slate-200">
          {USECASES.map((u, i) => (
            <div key={u.t} data-testid={`au-usecase-${i}`} className="bg-white p-10">
              <span className="text-xs font-black tabular-nums text-slate-400">0{i + 1}</span>
              <h3 className="text-2xl font-black tracking-tight text-[#0A1224] mt-6">{u.t}</h3>
              <p className="text-slate-600 mt-4 leading-relaxed">{u.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Honest framing */}
      <Section testid="au-framing" className="bg-[#0A1224] text-white">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Overline className="text-slate-400 mb-6">What's true today</Overline>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight">
              The Australia rollout is a programme, not a marketing claim.
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mt-6">
              Continuum's Canadian platform is in build. The AU programme begins in 2027 with
              a cohort of foundation customers across LGAs, PHNs, and philanthropies. We
              won't publish AU case studies, screenshots, or local indicator panels until
              they're real and approved by the partner.
            </p>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-white/[0.03] border border-white/15 p-8">
              <Overline className="text-slate-400 mb-4">2027 cohort scope</Overline>
              <ul className="space-y-3 text-slate-200">
                <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />6–10 foundation partners</li>
                <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Residency in Australian regions</li>
                <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Pricing locked for the cohort</li>
                <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />ACNC + ABS data integrations</li>
                <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Same versioned methodology engine</li>
              </ul>
            </div>
          </aside>
        </div>
      </Section>

      {/* What ships at parity */}
      <Section testid="au-parity">
        <Overline className="mb-6">Feature parity from day one</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-4xl mb-12">
          The AU cohort gets the full platform — not a regional cut.
        </h2>
        <StatLine items={[
          { label: "Methodology engine", value: "Same", note: "Versioned, lineage-traceable" },
          { label: "Data residency", value: "AU regions", note: "Hosted in Australia" },
          { label: "Cohort start", value: "2027", note: "Foundation customers first" },
          { label: "Pricing model", value: "AUD", note: "Published once cohort locks" },
        ]} />
      </Section>

      {/* Final CTA */}
      <section data-testid="au-final-cta" className="bg-[#0A1224] text-white py-24 md:py-32 relative">
        <ContinuumBand className="absolute top-0 left-0 right-0 h-1" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Overline className="text-slate-400 mb-6">Register interest</Overline>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-8">
            Sit on the 2027 cohort shortlist.
          </h2>
          <p className="text-slate-300 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            We're not running paid acquisition for AU. The cohort is filled from the waitlist.
            Tell us who you are; we'll be in touch when the programme opens.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <PrimaryCTA to="/contact" testid="au-final-walkthrough-cta">Register Australian interest</PrimaryCTA>
            <Link
              to="/"
              data-testid="au-back-canada-link"
              className="text-slate-300 hover:text-white inline-flex items-center gap-2 px-3 py-3"
            >
              Back to Continuum (Canada) <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Hidden / invariant footnote per spec §6 */}
      <Section testid="au-footnote" className="!py-12">
        <p className="text-xs text-slate-500 max-w-3xl">
          This page is unlinked from the main site. It's pre-built and routed at{" "}
          <span className="font-mono">/au</span> per the Continuum spec, ready to activate
          when the 2027 Australia runway opens.
        </p>
      </Section>
    </>
  );
}
